import React from 'react';
import { Badge, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { AttendanceRecord, CameraInfo, ChildInfo } from '../types/dashboard';
import { formatTimeInNurseryTimezone } from '../utils/timezone';

interface DetailListProps {
  type: 'cameras' | 'children' | 'present' | 'absent' | 'vacation' | 'holiday';
  data?: CameraInfo[] | ChildInfo[] | AttendanceRecord[];
  nurseryTimezone: string;
}

const DetailList: React.FC<DetailListProps> = ({ type, data, nurseryTimezone }) => {
  const { t } = useTranslation('components', { keyPrefix: 'detailList' });
  const listData = Array.isArray(data) ? data : [];

  const formatTime = (timeString: string | null) => {
    if (!timeString) return t('na');
    
    try {
      return formatTimeInNurseryTimezone(timeString, nurseryTimezone);
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };


  const renderCameraList = (cameraData: CameraInfo[]) => (
    <div className="table-responsive">
      <Table size="sm" className="mb-0 detail-list-table">
        <thead>
          <tr>
            <th>{t('camera')}</th>
            <th>{t('name')}</th>
          </tr>
        </thead>
        <tbody>
          {cameraData.map((item: CameraInfo, index: number) => (
            <tr key={index}>
              <td>
                <code className="text-primary">{item.camera_number}</code>
              </td>
              <td>{item.name}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderChildList = (childData: ChildInfo[]) => (
    <div className="table-responsive">
      <Table size="sm" className="mb-0 detail-list-table">
        <thead>
          <tr>
            <th>{t('id')}</th>
            <th>{t('name')}</th>
          </tr>
        </thead>
        <tbody>
          {childData.map((item: ChildInfo, index: number) => (
            <tr key={index}>
              <td>
                <code className="text-primary">{item.child_id || item.id || '-'}</code>
              </td>
              <td>{item.name}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderAttendanceList = (attendanceData: AttendanceRecord[]) => (
    <div className="table-responsive">
      <Table size="sm" className="mb-0 detail-list-table">
        <thead>
          <tr>
            <th>{t('child')}</th>
            <th>{t('classroom')}</th>
            <th>{t('dropoff')}</th>
            <th>{t('pickup')}</th>
          </tr>
        </thead>
        <tbody>
          {attendanceData.map((item: AttendanceRecord, index: number) => (
            <tr key={index}>
              <td className="fw-medium">{item.child}</td>
              <td>
                <Badge bg="light" text="dark" className="fw-normal">
                  {item.classroom}
                </Badge>
              </td>
              <td className="text-muted">
                {type === 'holiday' ? (
                  <Badge bg="success">{t('holiday')}</Badge>
                ) : item.check_in === 'vacation' ? (
                  <Badge bg="warning" text="dark">{t('vacation')}</Badge>
                ) : (
                  formatTime(item.check_in)
                )}
              </td>
              <td className="text-muted">
                {type === 'holiday' ? (
                  <Badge bg="success">{t('holiday')}</Badge>
                ) : item.check_out === 'vacation' ? (
                  <Badge bg="warning" text="dark">{t('vacation')}</Badge>
                ) : (
                  formatTime(item.check_out)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderContent = () => {
    switch (type) {
      case 'cameras':
        return renderCameraList(listData as CameraInfo[]);
      case 'children':
        return renderChildList(listData as ChildInfo[]);
      case 'present':
      case 'absent':
      case 'vacation':
      case 'holiday':
        return renderAttendanceList(listData as AttendanceRecord[]);
      default:
        return null;
    }
  };

  if (listData.length === 0) {
    return (
      <div className="text-center py-3">
        <p className="text-muted mb-0">{t('noDataAvailable')}</p>
      </div>
    );
  }

  return (
    <div>
      {renderContent()}
    </div>
  );
};

export default DetailList;
